// Áudio da ligação de WhatsApp no navegador do atendente.
//
// Formato do motor Go: Float32Array little-endian, 16 kHz, mono, frames de
// 60 ms, por WebSocket binário. O AudioContext roda a 16 kHz e o navegador
// reamostra microfone e alto-falante sozinho.
//
// prepare() precisa acontecer no clique (Ligar/Atender) por causa da política
// de autoplay; connect() vem depois, quando o backend devolve o id da ligação.

const SAMPLE_RATE = 16000;
const FRAME = 960;

const WORKLET = `
class Capture extends AudioWorkletProcessor {
  constructor() { super(); this.buf = new Float32Array(${FRAME}); this.n = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) for (let i = 0; i < ch.length; i++) {
      this.buf[this.n++] = ch[i];
      if (this.n === ${FRAME}) { this.port.postMessage(this.buf.slice(0)); this.n = 0; }
    }
    return true;
  }
}
class Playback extends AudioWorkletProcessor {
  constructor() {
    super(); this.q = []; this.off = 0; this.size = 0; this.started = false;
    this.port.onmessage = e => {
      this.q.push(e.data); this.size += e.data.length;
      // no máximo 500 ms de atraso: descarta o mais antigo
      while (this.size > ${SAMPLE_RATE / 2} && this.q.length > 1) { this.size -= this.q.shift().length - this.off; this.off = 0; }
    };
  }
  process(_, outputs) {
    const out = outputs[0][0];
    if (!this.started && this.size >= ${FRAME * 2}) this.started = true;
    for (let i = 0; i < out.length; i++) {
      if (!this.started || !this.q.length) { out[i] = 0; continue; }
      const head = this.q[0];
      out[i] = head[this.off++]; this.size--;
      if (this.off >= head.length) { this.q.shift(); this.off = 0; }
    }
    if (!this.q.length) this.started = false;
    return true;
  }
}
registerProcessor('wa-capture', Capture);
registerProcessor('wa-playback', Playback);`;

export function describeMicError(err) {
  if (err?.name === "NotFoundError") return "nenhum microfone encontrado neste computador";
  if (err?.name === "NotAllowedError") return "permissão do microfone negada (cadeado na barra de endereço)";
  if (err?.name === "NotReadableError") return "o microfone está em uso por outro programa";
  return err?.message || "erro desconhecido";
}

export function callAudioSupported() {
  return Boolean(window.isSecureContext && navigator.mediaDevices?.getUserMedia && window.AudioWorkletNode);
}

export class WhatsAppCallAudio {
  constructor() {
    this.ctx = null;
    this.stream = null;
    this.player = null;
    this.micSource = null;
    this.ws = null;
  }

  get hasMic() {
    return Boolean(this.stream);
  }

  get connected() {
    return this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING;
  }

  async prepare({ listenOnly = false } = {}) {
    if (this.ctx) return;
    if (!callAudioSupported()) {
      throw new Error("navegador sem suporte a áudio de ligação (use Chrome ou Edge)");
    }
    if (!listenOnly) {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    }
    try {
      const ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
      const url = URL.createObjectURL(new Blob([WORKLET], { type: "application/javascript" }));
      await ctx.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);

      if (this.stream) {
        const capture = new AudioWorkletNode(ctx, "wa-capture");
        capture.port.onmessage = (e) => {
          if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(e.data.buffer);
        };
        const silent = ctx.createGain();
        silent.gain.value = 0; // mantém a captura rodando sem tocar o próprio microfone
        this.micSource = ctx.createMediaStreamSource(this.stream);
        this.micSource.connect(capture).connect(silent).connect(ctx.destination);
      }

      this.player = new AudioWorkletNode(ctx, "wa-playback", { outputChannelCount: [1] });
      this.player.connect(ctx.destination);
      await ctx.resume();
      this.ctx = ctx;
    } catch (err) {
      this.stop();
      throw err;
    }
  }

  connect(url, protocols) {
    try { this.ws?.close(); } catch {}
    const ws = new WebSocket(url, protocols);
    ws.binaryType = "arraybuffer";
    ws.onmessage = (e) => {
      if (e.data instanceof ArrayBuffer) this.player?.port.postMessage(new Float32Array(e.data));
    };
    this.ws = ws;
  }

  // Cada lado num fluxo próprio, para a transcrição (callTranscriber.js). No mudo
  // o microfone vira silêncio também aqui. Sem microfone, só o cliente.
  recordingStreams() {
    if (!this.ctx || !this.player) return null;
    const client = this.ctx.createMediaStreamDestination();
    this.player.connect(client);
    let agent = null;
    if (this.micSource) {
      agent = this.ctx.createMediaStreamDestination();
      this.micSource.connect(agent);
    }
    return {
      agent: agent?.stream || null,
      client: client.stream,
      release: () => {
        try { this.player?.disconnect(client); } catch {}
        try { if (agent) this.micSource?.disconnect(agent); } catch {}
      },
    };
  }

  setMuted(muted) {
    this.stream?.getAudioTracks().forEach((track) => {
      track.enabled = !muted;
    });
  }

  stop() {
    try { this.ws?.close(); } catch {}
    this.stream?.getTracks().forEach((track) => track.stop());
    try { this.ctx?.close(); } catch {}
    this.ws = null;
    this.stream = null;
    this.ctx = null;
    this.player = null;
    this.micSource = null;
  }
}
